import { useCallback, useContext, useEffect, useMemo, useState } from "react";

//Components
import TicketAndWarning from "./TicketAndWarning/TicketAndWarning";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AppBar, Tabs, Tab, Typography, Box } from "@material-ui/core";

//Styling
import { makeStyles } from "@material-ui/core/styles";
import { withStyles } from "@material-ui/core/styles";

//Utils
import PropTypes from "prop-types";
import { UserContext } from "../Contexts/UserContext";
import { createEmptyVis, createEmptyTicket } from "../utils/EmptyObjects";
import {
  isRaceDataPresent,
  parseObjectArrayIdString,
  ticketStatusHash,
} from "../utils/utils";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { MutationContext } from "../Contexts/MutationContext";
import { SetupContext } from "../Contexts/SetupContext";

const autoCreateTicketLockKey = incidentId =>
  `ticket:auto-create:${incidentId}`;

const getAutoCreateTicketLock = incidentId => {
  try {
    return window.sessionStorage.getItem(autoCreateTicketLockKey(incidentId));
  } catch (e) {
    return null;
  }
};

const setAutoCreateTicketLock = (incidentId, value) => {
  try {
    window.sessionStorage.setItem(autoCreateTicketLockKey(incidentId), value);
  } catch (e) {
    // no-op if storage is unavailable
  }
};

const clearAutoCreateTicketLock = incidentId => {
  try {
    window.sessionStorage.removeItem(autoCreateTicketLockKey(incidentId));
  } catch (e) {
    // no-op if storage is unavailable
  }
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box style={{ padding: "0px" }} p={1}>
          <Typography component={"div"}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `scrollable-auto-tab-${index}`,
    "aria-controls": `scrollable-auto-tabpanel-${index}`,
  };
}
const StyledTab = withStyles(() => ({
  root: {
    color: props =>
      props.nightmode === "true"
        ? props.selected
          ? "white"
          : "#bbbbbb"
        : "inherit",
  },
}))(props => <Tab {...props} />);

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
}));

const TicketsAndWarningsTabs = ({
  incidentIndex,
  incidentId,
  incidentUuid,
  incidentTickets,
  ticketsLoading,
  ticketsError,
  ticketsData,
  piss,
  incidentPeople,
  incidentOfficers,
  incidentOriId,
  viss,
  getVehiclesInfo,
  primaryOfficer,
  userRoles,
  setPausePolling,
  setPopper,
  _ticketAndWarningIsMounted,
  memoizedIsOnIncident,
  ...props
}) => {
  const classes = useStyles();
  const [ticketTab, setTicketTab] = useState(0);
  const [addTicketDisabled, setAddTicketDisabled] = useState(false);
  const { user, nightMode, onlineState } = useContext(UserContext);
  const { incidentDispatch, incidents } = useContext(IncidentsContext);
  const { UPDATE_INCIDENT } = INCIDENT_ACTIONS;
  const { updateTicketAllFields, updateStatewideEvents } =
    useContext(MutationContext);
  const { setup } = useContext(SetupContext);

  const { stateWideEvents } = setup.setupLists;

  const handleChange = (event, newValue) => {
    setTicketTab(newValue);
  };

  useEffect(() => {
    _ticketAndWarningIsMounted.current = true;
    return () => {
      // ComponentWillUnmount in Class Component
      _ticketAndWarningIsMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viss.length === 0) {
      if (!onlineState && import.meta.env.VITE_FORCE_ONLINE !== "true") {
        console.log("Adding an empty vis in TicketsAndWarningsTabs");
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incidentId,
            uuid: incidentUuid,
            property: "vehicleIncidentSpecifics",
            value: [createEmptyVis()],
          },
        });
      } else {
        getVehiclesInfo();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      incidentTickets?.length === 0 &&
      !onlineState &&
      import.meta.env.VITE_FORCE_ONLINE !== "true"
    ) {
      const incidentKey = incidentId || incidentUuid;
      const lockState = getAutoCreateTicketLock(incidentKey);
      if (lockState === "pending" || lockState === "done") return;
      setAutoCreateTicketLock(incidentKey, "pending");
      //add an empty ticket to the array
      const localTicket = createEmptyTicket(
        incidents[incidentIndex]?.callTime,
        incidents[incidentIndex]?.address,
        incidents[incidentIndex]
      );
      let input = {
        incidentId: incidentId,
        originatorId: user?.id,
        ticketId: localTicket.id,
        issuedAt: incidents[incidentIndex]?.callTime
          ? incidents[incidentIndex]?.callTime
          : null,
        violationDate: incidents[incidentIndex]?.callTime
          ? incidents[incidentIndex]?.callTime
          : null,
        ticketLocationId: incidents[incidentIndex]?.address
          ? incidents[incidentIndex]?.address.id
          : null,
      };
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incidentId,
          uuid: incidentUuid,
          property: "incidentTickets",
          value: [localTicket],
        },
      });
      updateTicketAllFields({
        variables: { input: input },
        context: { serializationKey: "incidentQueue" },
      })
        .then(() => {
          setAutoCreateTicketLock(incidentKey, "done");
        })
        .catch(() => {
          clearAutoCreateTicketLock(incidentKey);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disableTicketAddition = () => {
    setAddTicketDisabled(true);

    setTimeout(() => {
      setAddTicketDisabled(false);
    }, 1500);
  };

  const raceDataPresent = useMemo(() => {
    return incidentTickets?.some(ticket => isRaceDataPresent(ticket));
  }, [incidentTickets]);

  const raceDataCodeIsPresent = useMemo(() => {
    const events = incidents[incidentIndex]?.stateWideEvents.filter(
      event => event?.name !== "None"
    );
    const raceDataCode = stateWideEvents.find(e =>
      e?.name.includes("Race Data Present")
    );
    return events.some(event => event?.id === raceDataCode?.id);
  }, [incidentIndex, incidents, stateWideEvents]);

  const removeRaceDataPresent = useCallback(() => {
    let events = incidents[incidentIndex]?.stateWideEvents.filter(
      event => !event?.name.includes("Race Data Present")
    );
    if (!events || events?.length === 0) {
      const none = stateWideEvents.find(event => event?.name?.includes("None"));
      events = [none];
    }
    const eventsIds = parseObjectArrayIdString(events);
    updateStatewideEvents({
      variables: {
        input: {
          incidentId: incidentId ? incidentId : incidentUuid,
          originatorId: user?.id,
          stateWideEventIds: eventsIds,
        },
      },
    });
    incidentDispatch({
      type: UPDATE_INCIDENT,
      payload: {
        id: incidentId,
        uuid: incidentUuid,
        property: "stateWideEvents",
        value: events,
      },
    });
  }, [
    UPDATE_INCIDENT,
    incidentDispatch,
    incidentId,
    incidentIndex,
    incidentUuid,
    incidents,
    stateWideEvents,
    updateStatewideEvents,
    user?.id,
  ]);

  useEffect(() => {
    const addRaceDataPresent = () => {
      const events = incidents[incidentIndex]?.stateWideEvents.filter(
        event => event?.name !== "None"
      );
      const raceDataCode = stateWideEvents.find(e =>
        e.name.includes("Race Data Present")
      );
      const allEvents = [...events, raceDataCode];
      const allEventsIds = parseObjectArrayIdString(allEvents);
      updateStatewideEvents({
        variables: {
          input: {
            incidentId: incidentId ? incidentId : incidentUuid,
            originatorId: user?.id,
            stateWideEventIds: allEventsIds,
          },
        },
      });
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incidentId,
          uuid: incidentUuid,
          property: "stateWideEvents",
          value: allEvents,
        },
      });
    };
    //require the user to be an officer on the incident for this hook to fire
    if (memoizedIsOnIncident) {
      //if there is race data present, add the code to the incident if it's not already present
      if (raceDataPresent && !raceDataCodeIsPresent) {
        console.log("Adding race data present code");
        addRaceDataPresent();
      } else if (!raceDataPresent && raceDataCodeIsPresent) {
        console.log("Removing race data is present code");
        removeRaceDataPresent();
      } else {
        return;
      }
    } else {
      return;
    }
  }, [
    raceDataPresent,
    raceDataCodeIsPresent,
    memoizedIsOnIncident,
    incidents,
    incidentIndex,
    stateWideEvents,
    updateStatewideEvents,
    incidentId,
    incidentUuid,
    user?.id,
    incidentDispatch,
    UPDATE_INCIDENT,
    removeRaceDataPresent,
  ]);

  // if (onlineState && ticketsLoading) return <div><CircularProgress data-cy="loading-circle" /></div>
  if (ticketsError)
    return (
      <div>
        {ticketsError.graphQLErrors.map((e, i) => {
          return <p key={i}>{e.message}</p>;
        })}
      </div>
    );
  if (incidentTickets) {
    return (
      <div className={classes.root}>
        <AppBar position="static" color="default">
          <Tabs
            value={ticketTab}
            onChange={handleChange}
            indicatorColor="primary"
            //textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable auto tabs example"
          >
            {incidentTickets.map((ticket, index) => {
              const ticketColor =
                ticket?.currentStatus >= 4
                  ? "green"
                  : nightMode
                  ? "white"
                  : "inherit";
              return (
                <StyledTab
                  nightmode={nightMode?.toString()}
                  key={ticket.id}
                  label={
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span
                        style={{ color: ticketColor }}
                        className="MuiTab-root"
                      >
                        Ticket ID: {ticket.id}
                      </span>
                      <span
                        style={{ color: ticketColor }}
                        className="MuiTab-root"
                      >
                        Status: {ticketStatusHash[ticket.currentStatus || 1]}
                      </span>
                    </div>
                  }
                  {...a11yProps(index)}
                />
              );
            })}
          </Tabs>
        </AppBar>
        {incidentTickets.map((ticket, index) => {
          //check all tickets to see if each has !ticket?.ticketViolationPrototype?.id || !ticket?.ticketViolationPrototype?.fullName
          //then pass that boolean value as a prop to TicketAndWarning
          const hasBlankTicket = incidentTickets.some(
            ticket =>
              !ticket?.ticketViolationPrototype?.id ||
              !ticket?.ticketViolationPrototype?.fullName
          );
          return (
            <TabPanel key={ticket.id} value={ticketTab} index={index}>
              <TicketAndWarning
                setPopper={setPopper}
                ticket={ticket}
                piss={piss}
                incidentPeople={incidentPeople}
                incidentIndex={incidentIndex}
                incidentId={incidentId}
                incidentUuid={incidentUuid}
                ticketsLoading={ticketsLoading}
                ticketsError={ticketsError}
                ticketsData={ticketsData}
                viss={viss}
                incidentOfficers={incidentOfficers}
                ticketTab={ticketTab}
                setTicketTab={setTicketTab}
                ticketsLength={incidentTickets.length}
                incidentOriId={incidentOriId}
                primaryOfficer={primaryOfficer}
                userRoles={userRoles}
                setPausePolling={setPausePolling}
                disableTicketAddition={disableTicketAddition}
                addTicketDisabled={addTicketDisabled}
                hasBlankTicket={hasBlankTicket}
                {...props}
              />
            </TabPanel>
          );
        })}
      </div>
    );
  } else {
    return (
      <div>
        <CircularProgress data-cy="loading-circle" />
      </div>
    );
  }
};

export default TicketsAndWarningsTabs;
